import { endOfDay, startOfDay } from 'date-fns';
import { useTypedQuery } from '../lib/generated/zeus/apollo';
import { queryRosterResponseSelector } from './selectors/team-roster';
import type { DerivedTeamRoster } from './types';
import type { ApolloError } from '@apollo/client';
import { objValueNullToUndefined, compact } from '../lib/utils';
import { genericScalars } from '../lib/graphql/helpers';

const queryTeamRosterAuditName = 'queryTeamRosterAudit';

type UseQueryTeamRosterAuditParamsFuncParams = {
  readonly onCompleted?: (d: readonly DerivedTeamRoster[], msg: string) => void;
  readonly onError: (e: ApolloError) => void;
};

type UseQueryTeamRosterAuditParams = UseQueryTeamRosterAuditParamsFuncParams & {
  readonly teamIds: readonly string[];
  readonly teamTypes: readonly string[];
  readonly costObjectIds: readonly string[];
  readonly teamRoleIds: readonly string[];
  readonly teamRoleGroups: readonly string[];
  readonly userIds: readonly string[];
  readonly userTypes: readonly string[];
  readonly from: Date;
  readonly to: Date;
};

/**
 * Query team roster by following params.
 * @param teamIds - selected team ids
 * @param teamTypes - selected team types
 * @param teamRoleIds - selected team role ids
 * @param teamRoleGroups - selected team role groups
 * @param userIds - selected user ids
 * @param from - start date of the date range for shifts
 * @param to - emd date of the date range for shifts
 * @returns team roster with response message.
 */
const useQueryTeamRosterAudit = ({
  teamIds,
  teamTypes,
  costObjectIds,
  teamRoleIds,
  teamRoleGroups,
  userIds,
  userTypes,
  from,
  to,
  onCompleted,
  onError,
}: UseQueryTeamRosterAuditParams) =>
  useTypedQuery(
    {
      queryTeamRosterAudit: [
        {
          teamIds: [...teamIds],
          teamTypes: [...teamTypes],
          ions: [...costObjectIds],
          teamRoleIds: [...teamRoleIds],
          teamRoleGroups: [...teamRoleGroups],
          userIds: [...userIds],
          userTypes: [...userTypes],
          fromDate: startOfDay(from),
          toDate: endOfDay(to),
        },
        queryRosterResponseSelector,
      ],
    },
    {
      scalars: genericScalars,
      apolloOptions: {
        defaultOptions: { fetchPolicy: 'no-cache' },
        onCompleted: (data) => {
          if (onCompleted !== undefined) {
            const queriedShiftEvent: readonly DerivedTeamRoster[] = compact(
              data.queryTeamRosterAudit.data.map((teamRoster) =>
                objValueNullToUndefined<DerivedTeamRoster>(teamRoster)
              )
            );

            onCompleted(queriedShiftEvent, data.queryTeamRosterAudit.message);
          }
        },
        onError,
      },
      operationOptions: { operationName: queryTeamRosterAuditName },
    }
  );

export default useQueryTeamRosterAudit;
